/**
 * Client API — wrapper `fetch` con Bearer token e gestione uniforme degli errori.
 *
 * Design ref: "Error handling" del frontend — il client intercetta `401`
 * avviando il re‑login OIDC e `403` mostrando un messaggio di accesso negato.
 * L'autorizzazione autorevole resta l'API: le guardie lato client sono
 * difensive, ma è l'API a negare qualsiasi accesso admin con HTTP `403`
 * indipendentemente dal `Role`. *Validates: 4.5.*
 *
 * Responsabilità:
 *  - allega `Authorization: Bearer <token>` leggendo l'access token dall'auth
 *    context (`getAccessToken`);
 *  - risolve il base URL dalla config runtime (`apiBaseUrl` da `config.json`);
 *  - fa il parsing dell'inviluppo di errore `{ error: { code, message } }`;
 *  - su `401` invoca l'handler di re‑login (avvio flusso OIDC);
 *  - su `403` solleva un errore marcato come "accesso negato".
 */
import type { ApiError } from '../shared/dto';
import { loadConfig } from '../config';

/** Restituisce l'access token corrente (o `null` se non autenticato). */
export type GetAccessToken = () => Promise<string | null>;

/** Callback invocata quando l'API risponde `401` (re‑login OIDC). */
export type OnUnauthenticated = () => void | Promise<void>;

/** Dipendenze iniettabili del client (facilitano il testing). */
export interface ApiClientDeps {
  /** Sorgente dell'access token (dall'auth context). */
  getAccessToken: GetAccessToken;
  /** Handler di re‑login, invocato su risposta `401`. */
  onUnauthenticated?: OnUnauthenticated;
  /** `fetch` iniettabile (default: `globalThis.fetch`). */
  fetchImpl?: typeof fetch;
  /**
   * Risolutore del base URL dell'API. Default: `apiBaseUrl` da `config.json`.
   * Iniettabile per i test.
   */
  resolveBaseUrl?: () => Promise<string>;
}

/** Categorie di errore riconosciute dal client, utili per la UI. */
export type ApiErrorKind =
  | 'unauthenticated' // 401 — sessione assente/scaduta → re‑login OIDC
  | 'forbidden' // 403 — accesso negato (autorevole lato API)
  | 'validation' // 400 — payload/valore non valido
  | 'not_found' // 404 — risorsa inesistente
  | 'conflict' // 409 — conflitto (es. vincolo unique)
  | 'unavailable' // 503 — dipendenza non disponibile (es. Mongo)
  | 'server' // 5xx generico
  | 'network' // errore di rete / risposta non parsabile
  | 'unknown';

/**
 * Errore normalizzato sollevato dal client API. Espone `status`, il `code`
 * dell'inviluppo (quando presente) e una `kind` per il branching nella UI.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly kind: ApiErrorKind;

  constructor(params: {
    message: string;
    status: number;
    code?: string | null;
    kind: ApiErrorKind;
  }) {
    super(params.message);
    this.name = 'ApiRequestError';
    this.status = params.status;
    this.code = params.code ?? null;
    this.kind = params.kind;
  }

  /** `true` per un accesso negato (`403`). */
  get isAccessDenied(): boolean {
    return this.kind === 'forbidden';
  }

  /** `true` per una sessione non autenticata (`401`). */
  get isUnauthenticated(): boolean {
    return this.kind === 'unauthenticated';
  }
}

/** Mappa uno status HTTP nella categoria d'errore corrispondente. */
function kindFromStatus(status: number): ApiErrorKind {
  switch (status) {
    case 401:
      return 'unauthenticated';
    case 403:
      return 'forbidden';
    case 400:
      return 'validation';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 503:
      return 'unavailable';
    default:
      return status >= 500 ? 'server' : 'unknown';
  }
}

/** Type guard sull'inviluppo di errore `{ error: { code, message } }`. */
function isApiError(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) return false;
  const err = (value as { error?: unknown }).error;
  if (typeof err !== 'object' || err === null) return false;
  const { code, message } = err as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}

/** Opzioni di una richiesta (superset ristretto di `RequestInit`). */
export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Corpo JSON serializzabile; serializzato con `JSON.stringify`. */
  json?: unknown;
  /** Corpo grezzo (alternativo a `json`). */
  body?: BodyInit | null;
}

const DEFAULT_RESOLVE_BASE_URL = async (): Promise<string> => {
  const config = await loadConfig();
  return config.apiBaseUrl;
};

/** Concatena base URL e path evitando slash doppi o mancanti. */
function joinUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = baseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/**
 * Crea un client API con le dipendenze fornite. Tipicamente usato da un hook
 * (`useApiClient`) che passa `getAccessToken`/`login` dall'auth context.
 */
export function createApiClient(deps: ApiClientDeps) {
  const {
    getAccessToken,
    onUnauthenticated,
    fetchImpl = globalThis.fetch.bind(globalThis),
    resolveBaseUrl = DEFAULT_RESOLVE_BASE_URL,
  } = deps;

  /**
   * Esegue una richiesta e restituisce il corpo JSON deserializzato come `T`.
   * Solleva `ApiRequestError` per ogni risposta non 2xx; su `401` invoca prima
   * l'handler di re‑login.
   */
  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { json, body, headers, ...rest } = options;

    const baseUrl = await resolveBaseUrl();
    const url = joinUrl(baseUrl, path);

    const finalHeaders = new Headers(headers);
    const token = await getAccessToken();
    if (token) {
      finalHeaders.set('Authorization', `Bearer ${token}`);
    }

    let finalBody: BodyInit | null | undefined = body;
    if (json !== undefined) {
      finalHeaders.set('Content-Type', 'application/json');
      finalBody = JSON.stringify(json);
    }
    if (!finalHeaders.has('Accept')) {
      finalHeaders.set('Accept', 'application/json');
    }

    let response: Response;
    try {
      response = await fetchImpl(url, { ...rest, headers: finalHeaders, body: finalBody });
    } catch (cause) {
      throw new ApiRequestError({
        message: 'Errore di rete durante la richiesta API.',
        status: 0,
        kind: 'network',
      });
    }

    if (response.ok) {
      return parseSuccess<T>(response);
    }

    // Risposta di errore: normalizza e, su 401, avvia il re‑login.
    if (response.status === 401 && onUnauthenticated) {
      await onUnauthenticated();
    }
    throw await buildError(response);
  }

  /** Deserializza un corpo di successo; `204`/corpo vuoto → `undefined as T`. */
  async function parseSuccess<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (text.length === 0) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiRequestError({
        message: 'Risposta API non è JSON valido.',
        status: response.status,
        kind: 'network',
      });
    }
  }

  /** Costruisce un `ApiRequestError` dall'inviluppo `{ error }` quando presente. */
  async function buildError(response: Response): Promise<ApiRequestError> {
    const kind = kindFromStatus(response.status);
    let code: string | null = null;
    let message = defaultMessage(kind, response.status);

    try {
      const text = await response.text();
      if (text.length > 0) {
        const parsed: unknown = JSON.parse(text);
        if (isApiError(parsed)) {
          code = parsed.error.code;
          message = parsed.error.message || message;
        }
      }
    } catch {
      // Corpo non JSON: manteniamo il messaggio di default per la categoria.
    }

    return new ApiRequestError({ message, status: response.status, code, kind });
  }

  return {
    request,
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, json?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'POST', json }),
    put: <T>(path: string, json?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PUT', json }),
    patch: <T>(path: string, json?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PATCH', json }),
    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'DELETE' }),
  };
}

/** Messaggio di default (localizzato) per categoria d'errore. */
function defaultMessage(kind: ApiErrorKind, status: number): string {
  switch (kind) {
    case 'unauthenticated':
      return 'Sessione non valida o scaduta. È necessario effettuare nuovamente l\'accesso.';
    case 'forbidden':
      return 'Accesso negato: non hai i permessi per questa operazione.';
    case 'validation':
      return 'Richiesta non valida.';
    case 'not_found':
      return 'Risorsa non trovata.';
    case 'conflict':
      return 'Conflitto con lo stato corrente della risorsa.';
    case 'unavailable':
      return 'Servizio temporaneamente non disponibile. Riprova più tardi.';
    case 'server':
      return 'Errore interno del server.';
    default:
      return `Errore imprevisto (HTTP ${status}).`;
  }
}

/** Tipo del client restituito da `createApiClient`. */
export type ApiClient = ReturnType<typeof createApiClient>;
